import { useHandler } from "@redotech/react-util/hook";
import {
  ParcelConditionKind,
  parcelConditionKindToLabel,
} from "@redotech/redo-conversion-core-common/checkouts/parcel/parcel-tree";
import { RcoCheckoutPackageConditionStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import {
  NumberComparisonInequalityOperator,
  NumberComparisonOperator,
  numberComparisonInequalityOperators,
  numberComparisonOperators,
} from "@redotech/redo-flows-core-common/comparison-operators";
import { ListToListMatchType } from "@redotech/redo-flows-core-common/condition";
import { numberComparisonToLabel } from "@redotech/redo-flows-core-schema-common/expressions/number-expression";
import { useTranslation } from "@redotech/redo-i18n";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  RedoListItem,
  TextRedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { isDef } from "@redotech/util/checks";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo } from "react";
import { ConditionInput } from "./common/condition-inputs";
import { ListToListMatchTypeSelect } from "./common/list-to-list-match-type-select";
import { NumberMatchKindSelect } from "./common/number-match-kind-select";
import { ProductTagsSelector } from "./common/product-tags-selector";
import { RequireExactMatchInput } from "./common/require-exact-match-input";
import { SingleNumberStepInput } from "./common/single-number-step-input";
import { WeightStepInput } from "./common/weight-step-input";
import {
  FrontendStepType,
  PartialWithRequiredType,
  StepTypeDetailsProps,
} from "./index";

const RcoCheckoutPackageConditionInspector = memo(
  function RcoCheckoutPackageConditionInspector({
    step,
    setStep,
    onClose,
  }: StepTypeDetailsProps<
    PartialWithRequiredType<RcoCheckoutPackageConditionStep>
  >) {
    const { t } = useTranslation();
    const { selectConditionParams } = useConditionKindOptions({
      step,
      setStep,
    });

    const { line1Fields, line2Fields, line3Fields } = useConditionLineFields({
      step,
      setStep,
    });

    return (
      <Flex dir="column">
        <Flex align="center" justify="space-between" pt="md">
          <Text fontSize="md" fontWeight="semibold" textColor="primary">
            {t("Condition")}
          </Text>
          <RedoButton
            aria-label={t("Close")}
            hierarchy="tertiary"
            IconLeading={XIcon}
            onClick={onClose}
            size="sm"
          />
        </Flex>
        <ConditionInput<ParcelConditionKind>
          line1Fields={line1Fields}
          line2Fields={line2Fields}
          line3Fields={line3Fields}
          selectConditionParams={selectConditionParams}
        />
      </Flex>
    );
  },
);

function useConditionKindOptions({
  step,
  setStep,
}: {
  step: PartialWithRequiredType<RcoCheckoutPackageConditionStep>;
  setStep: (
    step: PartialWithRequiredType<RcoCheckoutPackageConditionStep>,
  ) => void;
}) {
  const conditionKindOptions:
    | TextRedoListItem<ParcelConditionKind>[]
    | undefined = useMemo(() => {
    return Object.values(ParcelConditionKind).map((kind) => ({
      type: "text",
      value: kind,
      text: parcelConditionKindToLabel[kind],
      id: kind,
    }));
  }, []);

  const setConditionKind = useHandler((conditionKind: ParcelConditionKind) => {
    switch (conditionKind) {
      case ParcelConditionKind.WEIGHT_OZ:
        setStep({ ...step, condition: { kind: conditionKind } });
        break;
      case ParcelConditionKind.ITEM_COUNT:
        setStep({ ...step, condition: { kind: conditionKind } });
        break;
      case ParcelConditionKind.PRODUCT_TAGS:
        setStep({
          ...step,
          condition: { kind: conditionKind, requireExactMatch: true },
        });
        break;
      default:
        assertNever(conditionKind);
    }
  });

  const selectConditionParams = useMemo(() => {
    return {
      options: conditionKindOptions,
      conditionKind: step.condition?.kind,
      setConditionKind,
    };
  }, [conditionKindOptions, step, setConditionKind]);

  return { selectConditionParams };
}

function useConditionLineFields({
  step,
  setStep,
}: {
  step: PartialWithRequiredType<RcoCheckoutPackageConditionStep>;
  setStep: (
    step: PartialWithRequiredType<RcoCheckoutPackageConditionStep>,
  ) => void;
}): {
  line1Fields: React.ReactNode;
  line2Fields: React.ReactNode;
  line3Fields: React.ReactNode;
} {
  const weightConditionLineFields = useWeightConditionLineFields({
    step,
    setStep,
  });
  const itemCountConditionLineFields = useItemCountConditionLineFields({
    step,
    setStep,
  });
  const productTagConditionLineFields = useProductTagConditionLineFields({
    step,
    setStep,
  });

  switch (step.condition?.kind) {
    case ParcelConditionKind.WEIGHT_OZ:
      return weightConditionLineFields;
    case ParcelConditionKind.ITEM_COUNT:
      return itemCountConditionLineFields;
    case ParcelConditionKind.PRODUCT_TAGS:
      return productTagConditionLineFields;
    case undefined:
      return { line1Fields: null, line2Fields: null, line3Fields: null };
    default:
      assertNever(step.condition);
  }
}

function useWeightConditionLineFields({
  step,
  setStep,
}: {
  step: PartialWithRequiredType<RcoCheckoutPackageConditionStep>;
  setStep: (
    step: PartialWithRequiredType<RcoCheckoutPackageConditionStep>,
  ) => void;
}): {
  line1Fields: React.ReactNode;
  line2Fields: React.ReactNode;
  line3Fields: React.ReactNode;
} {
  const setMatchValue = useHandler((_matchValue: number) => {
    if (step.condition?.kind !== ParcelConditionKind.WEIGHT_OZ) {
      return;
    }
    setStep({
      ...step,
      condition: { ...step.condition, matchValue: _matchValue },
    });
  });

  const numericCompareOptions: RedoListItem<NumberComparisonInequalityOperator>[] =
    useMemo(
      () =>
        numberComparisonInequalityOperators.map((type) => ({
          value: type,
          type: "text",
          id: type,
          text: numberComparisonToLabel[type],
        })),
      [],
    );

  const setMatchType = useHandler(
    (matchType: NumberComparisonInequalityOperator) => {
      if (step.condition?.kind !== ParcelConditionKind.WEIGHT_OZ) {
        return;
      }
      setStep({ ...step, condition: { ...step.condition, matchType } });
    },
  );

  const line1Fields = useMemo(() => {
    if (step.condition?.kind !== ParcelConditionKind.WEIGHT_OZ) {
      return null;
    }
    return (
      <NumberMatchKindSelect<NumberComparisonInequalityOperator>
        options={numericCompareOptions}
        selectedMatchKind={step.condition?.matchType}
        setMatchKind={setMatchType}
      />
    );
  }, [numericCompareOptions, step, setMatchType]);

  const line2Fields = useMemo(() => {
    if (step.condition?.kind !== ParcelConditionKind.WEIGHT_OZ) {
      return null;
    }
    return (
      <WeightStepInput
        matchValue={step.condition?.matchValue}
        setMatchValue={setMatchValue}
      />
    );
  }, [step, setMatchValue]);

  return { line1Fields, line2Fields, line3Fields: null };
}

function useItemCountConditionLineFields({
  step,
  setStep,
}: {
  step: PartialWithRequiredType<RcoCheckoutPackageConditionStep>;
  setStep: (
    step: PartialWithRequiredType<RcoCheckoutPackageConditionStep>,
  ) => void;
}) {
  const numericCompareOptions: RedoListItem<NumberComparisonOperator>[] =
    useMemo(
      () =>
        numberComparisonOperators.map((type) => ({
          value: type,
          type: "text",
          id: type,
          text: numberComparisonToLabel[type],
        })),
      [],
    );

  const setMatchType = useHandler((matchType: NumberComparisonOperator) => {
    if (step.condition?.kind !== ParcelConditionKind.ITEM_COUNT) {
      return;
    }
    setStep({ ...step, condition: { ...step.condition, matchType } });
  });

  const setMatchValue = useHandler((matchValue: number) => {
    if (step.condition?.kind !== ParcelConditionKind.ITEM_COUNT) {
      return;
    }
    setStep({ ...step, condition: { ...step.condition, matchValue } });
  });

  const line1Fields = useMemo(() => {
    if (step.condition?.kind !== ParcelConditionKind.ITEM_COUNT) {
      return null;
    }
    return (
      <Flex align="center" dir="row" gap="md" wrap="wrap">
        <NumberMatchKindSelect<NumberComparisonOperator>
          options={numericCompareOptions}
          selectedMatchKind={step.condition?.matchType}
          setMatchKind={setMatchType}
        />
        <SingleNumberStepInput
          money={false}
          setValue={setMatchValue}
          stepInteger
          value={step.condition?.matchValue}
        />
      </Flex>
    );
  }, [numericCompareOptions, step, setMatchType, setMatchValue]);

  return { line1Fields, line2Fields: null, line3Fields: null };
}

function useProductTagConditionLineFields({
  step,
  setStep,
}: {
  step: PartialWithRequiredType<RcoCheckoutPackageConditionStep>;
  setStep: (
    step: PartialWithRequiredType<RcoCheckoutPackageConditionStep>,
  ) => void;
}) {
  const setMatchType = useHandler((matchType: ListToListMatchType) => {
    if (step.condition?.kind !== ParcelConditionKind.PRODUCT_TAGS) {
      return;
    }
    setStep({ ...step, condition: { ...step.condition, matchType } });
  });

  const setMatchValues = useHandler((matchValues: string[]) => {
    if (step.condition?.kind !== ParcelConditionKind.PRODUCT_TAGS) {
      return;
    }
    setStep({ ...step, condition: { ...step.condition, matchValues } });
  });

  const setRequireExactMatch = useHandler((requireExactMatch: boolean) => {
    if (step.condition?.kind !== ParcelConditionKind.PRODUCT_TAGS) {
      return;
    }
    setStep({ ...step, condition: { ...step.condition, requireExactMatch } });
  });

  const line1Fields = useMemo(() => {
    if (step.condition?.kind !== ParcelConditionKind.PRODUCT_TAGS) {
      return null;
    }
    return (
      <ListToListMatchTypeSelect
        fieldLabelIsPlural
        matchType={step.condition.matchType}
        onMatchTypeChange={setMatchType}
      />
    );
  }, [step, setMatchType]);

  const line2Fields = useMemo(() => {
    if (step.condition?.kind !== ParcelConditionKind.PRODUCT_TAGS) {
      return null;
    }
    return (
      <ProductTagsSelector
        onTagsSelected={setMatchValues}
        selectedTags={step.condition.matchValues ?? []}
      />
    );
  }, [step, setMatchValues]);

  const line3Fields = useMemo(() => {
    if (step.condition?.kind !== ParcelConditionKind.PRODUCT_TAGS) {
      return null;
    }
    return (
      <RequireExactMatchInput
        onRequireExactMatchChange={setRequireExactMatch}
        requireExactMatch={step.condition.requireExactMatch}
      />
    );
  }, [step, setRequireExactMatch]);

  return { line1Fields, line2Fields, line3Fields };
}

export const rcoCheckoutPackageConditionStepType: FrontendStepType<
  PartialWithRequiredType<RcoCheckoutPackageConditionStep>
> = {
  type: "custom",
  valid: (step) => {
    if (!step.condition?.kind) {
      return false;
    }

    switch (step.condition.kind) {
      case ParcelConditionKind.WEIGHT_OZ:
      case ParcelConditionKind.ITEM_COUNT:
        return (
          isDef(step.condition?.matchType) && isDef(step.condition?.matchValue)
        );
      case ParcelConditionKind.PRODUCT_TAGS:
        return (
          isDef(step.condition?.matchType) &&
          isDef(step.condition?.matchValues) &&
          step.condition.matchValues.length > 0
        );
      default:
        return false;
    }
  },
  customInspector: RcoCheckoutPackageConditionInspector,
};
